/**
 * WebSocket connection layer (framework-free, unit-testable).
 *
 * Owns the socket lifecycle only:
 * - authentication (subprotocol bearer token for native, cookies for web —
 *   resolved by the injected `getConnectionParams`)
 * - connection timeout and heartbeat ping every 30s
 * - auto-reconnect with exponential backoff + jitter
 * - fallback to polling after MAX_RECONNECT_ATTEMPTS failed reconnects
 * - auth-close (4001) → refresh → reconnect, or fall back + notify
 * - generation token so a superseded connect() never installs an orphan socket
 *
 * Parsed messages are handed to `onMessage`; routing them to the task/user
 * stores is the provider's job.
 */

import type { RefreshOutcome } from '../../services/api';
import { logDebug, warnDebug, type ConnectionState, type WebSocketMessage } from './types';

export const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds
export const INITIAL_RECONNECT_DELAY_MS = 1000; // 1 second
export const MAX_RECONNECT_DELAY_MS = 30_000; // 30 seconds
export const MAX_RECONNECT_ATTEMPTS = 5;
export const CONNECTION_TIMEOUT_MS = 10_000; // 10 seconds
/** Close code the server uses for an authentication failure. */
export const AUTH_FAILED_CLOSE_CODE = 4001;

// Standard WebSocket readyState values (not read from the global so tests
// can run without a WebSocket implementation).
const WS_CONNECTING = 0;
const WS_OPEN = 1;

/** The subset of the WebSocket API this layer uses. */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: any) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: any) => void) | null;
  onmessage: ((event: { data: any }) => void) | null;
}

export interface ConnectionParams {
  url: string;
  protocols?: string[];
}

export interface WebSocketConnectionOptions {
  createSocket: (url: string, protocols?: string[]) => SocketLike;
  /** Resolves the URL (+ auth subprotocols); null when no auth is available. */
  getConnectionParams: () => Promise<ConnectionParams | null>;
  /** Token refresh; 'unavailable' means the auth backend is briefly down. */
  refreshSession: () => Promise<RefreshOutcome>;
  notifyUnauthorized: () => void;
  onMessage: (message: WebSocketMessage) => void;
  onStateChange: (state: ConnectionState) => void;
  onFallbackChange: (useFallback: boolean) => void;
  /** Task ids to re-subscribe to once a socket opens. */
  getSubscribedTaskIds: () => Iterable<string>;
  /** Jitter source (injectable for tests). */
  random?: () => number;
}

/** Backoff delay before reconnect attempt `attempt` (1-based). */
export function reconnectDelayMs(attempt: number, random: number): number {
  return Math.min(
    INITIAL_RECONNECT_DELAY_MS * Math.pow(2, attempt - 1) + random * 1000,
    MAX_RECONNECT_DELAY_MS
  );
}

export class WebSocketConnection {
  private ws: SocketLike | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private isConnecting = false;
  private shouldReconnect = true;
  private pageHidden = false;
  private disabled = false;
  // Generation token: each connect() captures a value; disconnect() bumps it.
  // After any await, connect() bails if the gen has changed — kills orphan-socket races.
  private connectionGen = 0;
  private currentState: ConnectionState = 'disconnected';

  constructor(private readonly options: WebSocketConnectionOptions) {}

  get state(): ConnectionState {
    return this.currentState;
  }

  /** Whether reconnects are armed (false after an explicit disconnect()). */
  get reconnectArmed(): boolean {
    return this.shouldReconnect;
  }

  setDisabled(disabled: boolean): void {
    this.disabled = disabled;
  }

  /** Re-arm reconnects (done on mount, before the first connect()). */
  arm(): void {
    this.shouldReconnect = true;
  }

  /** Web: while the page is hidden a close does not schedule a reconnect. */
  setPageHidden(hidden: boolean): void {
    this.pageHidden = hidden;
  }

  /**
   * The page/app became visible again. Reconnects once, only when the socket
   * is closed with nothing scheduled ('disconnected': closed while hidden, or
   * stopped after MAX_RECONNECT_ATTEMPTS). That manual attempt restarts the
   * backoff schedule. When connected, connecting, or a backoff retry is
   * already pending ('reconnecting'), it does nothing.
   */
  resume(): void {
    this.pageHidden = false;
    if (this.disabled || !this.shouldReconnect) return;
    if (this.currentState !== 'disconnected') return;
    this.reconnectAttempt = 0;
    void this.connect();
  }

  /** Sends a JSON message if the socket is open; returns whether it was sent. */
  sendIfOpen(message: Record<string, unknown>): boolean {
    if (this.ws?.readyState === WS_OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  private setState(state: ConnectionState): void {
    this.currentState = state;
    this.options.onStateChange(state);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WS_OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        } catch (err) {
          console.warn('[WebSocket] Failed to send heartbeat:', err);
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  /** Reconnect with jittered backoff; fall back to polling after the cap. */
  private scheduleReconnect(): void {
    if (!this.shouldReconnect) {
      this.setState('disconnected');
      return;
    }
    this.reconnectAttempt++;

    if (this.reconnectAttempt > MAX_RECONNECT_ATTEMPTS) {
      warnDebug('[WebSocket] Max reconnection attempts reached, falling back to polling');
      this.setState('disconnected');
      this.options.onFallbackChange(true);
      return;
    }

    const delay = reconnectDelayMs(
      this.reconnectAttempt,
      (this.options.random ?? Math.random)()
    );

    logDebug(
      `[WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})`
    );
    this.setState('reconnecting');

    this.reconnectTimeout = setTimeout(() => {
      void this.connect();
    }, delay);
  }

  disconnect(): void {
    // Bump generation: any in-flight connect() awaiting a token will bail
    // when it resumes and detects gen mismatch, instead of installing a stale socket.
    this.connectionGen++;
    this.shouldReconnect = false;
    this.clearHeartbeat();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      try {
        this.ws.close(1000, 'Client disconnect');
      } catch {
        // Ignore close errors
      }
      this.ws = null;
    }

    this.setState('disconnected');
    this.isConnecting = false;
  }

  async connect(): Promise<void> {
    if (this.disabled) return;
    if (this.isConnecting) return;
    // Guard against both OPEN *and* CONNECTING — without the CONNECTING check,
    // a second connect() could race past while the first is still handshaking.
    if (
      this.ws &&
      (this.ws.readyState === WS_OPEN || this.ws.readyState === WS_CONNECTING)
    ) {
      return;
    }

    // Capture this attempt's generation. If disconnect() runs (or another connect()
    // supersedes us) while we're awaiting the token, we'll detect it and bail
    // rather than installing an orphan socket on top of the active one.
    const myGen = ++this.connectionGen;

    this.isConnecting = true;
    this.setState('connecting');

    const connection = await this.options.getConnectionParams();

    // Aborted while awaiting token (disconnect or newer connect ran)
    if (myGen !== this.connectionGen) {
      this.isConnecting = false;
      return;
    }

    if (!connection) {
      this.isConnecting = false;
      this.setState('disconnected');
      return;
    }

    try {
      const ws = this.options.createSocket(connection.url, connection.protocols);

      // Final race check before we install this socket as the active one.
      if (myGen !== this.connectionGen) {
        try { ws.close(1000, 'Superseded'); } catch { /* ignore */ }
        this.isConnecting = false;
        return;
      }

      this.ws = ws;

      logDebug('[WebSocket] Connecting to', connection.url);

      // Connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WS_OPEN) {
          warnDebug('[WebSocket] Connection timeout');
          ws.close();
        }
      }, CONNECTION_TIMEOUT_MS);

      ws.onopen = () => {
        // Stale-socket guard: if this socket has been superseded, close & bail.
        if (this.ws !== ws) {
          try { ws.close(1000, 'Superseded'); } catch { /* ignore */ }
          return;
        }
        clearTimeout(connectionTimeout);
        logDebug('[WebSocket] Connected');
        this.setState('connected');
        this.reconnectAttempt = 0;
        this.isConnecting = false;
        this.options.onFallbackChange(false);

        this.startHeartbeat();

        // Re-subscribe to any tasks
        for (const taskId of this.options.getSubscribedTaskIds()) {
          if (ws.readyState === WS_OPEN) {
            ws.send(JSON.stringify({ type: 'subscribe', task_id: taskId }));
          }
        }
      };

      ws.onclose = (event) => {
        // Stale-socket guard: if a newer socket has already taken over, ignore
        // this close event (don't clear heartbeat, don't trigger reconnect).
        // Allow processing when ws is null — that means disconnect() cleared it.
        if (this.ws !== ws && this.ws !== null) {
          logDebug(`[WebSocket] Stale socket close ignored: code=${event.code}`);
          return;
        }
        clearTimeout(connectionTimeout);
        this.clearHeartbeat();
        this.isConnecting = false;

        logDebug(`[WebSocket] Closed: code=${event.code}, reason=${event.reason}`);

        // If the page is hidden (web), don't reconnect until it's visible again
        if (this.pageHidden) {
          this.setState('disconnected');
          return;
        }

        if (event.code === AUTH_FAILED_CLOSE_CODE) {
          warnDebug('[WebSocket] Authentication failed');
          this.setState('disconnected');
          // Attempt a refresh and reconnect before giving up
          void (async () => {
            const outcome = await this.options.refreshSession();
            if (outcome === 'refreshed') {
              logDebug('[WebSocket] Auth refreshed, reconnecting');
              this.options.onFallbackChange(false);
              this.reconnectAttempt = 0;
              void this.connect();
              return;
            }
            if (outcome === 'unavailable') {
              // Auth backend blip: keep the session, retry with backoff.
              this.scheduleReconnect();
              return;
            }
            this.options.onFallbackChange(true);
            warnDebug('[WebSocket] Refresh failed after auth close');
            this.options.notifyUnauthorized();
          })();
          return;
        }

        // Includes 1013 (try again later): the server's session store is down.
        this.scheduleReconnect();
      };

      ws.onerror = (error) => {
        warnDebug('[WebSocket] Error:', error);
        // onclose will be called after onerror
      };

      ws.onmessage = (event) => {
        // Stale-socket guard: don't process events from a superseded socket.
        if (this.ws !== ws) return;
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          logDebug('[WebSocket] Message:', message.type, message.task_id || '');
          this.options.onMessage(message);
        } catch (err) {
          warnDebug('[WebSocket] Failed to parse message:', err);
        }
      };
    } catch (err) {
      warnDebug('[WebSocket] Failed to create connection:', err);
      this.isConnecting = false;
      this.setState('disconnected');
    }
  }
}
